/**
 * Getting started.
 *
 * The page the product owner asked for: "shows the steps and checklist the user
 * must complete, with instructions and links", reachable forever rather than
 * only on day one — because the people most lost in this product are the ones
 * who have been using it for six months.
 *
 * Three things happen here and nowhere else:
 *   1. The goal question, the first time anyone sees it, and every time
 *      afterwards that somebody wants to change the answer.
 *   2. The full derived checklist.
 *   3. Undismissing. Whatever the dashboard card hid, this page gives back.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { useOnboardingT } from '../lib/onboardingStrings';
import {
  useOnboarding,
  OnboardingChecklist,
  OnboardingGoalChooser,
  OnboardingProgressBar,
  OnboardingSkeleton,
  ONBOARDING_QUERY_KEY,
} from '../components/Onboarding';
import { Card, CardHeader, Button, ErrorNotice } from '../components/ui';
import { IconChevron } from '../components/icons';

export function GettingStartedPage() {
  const { tx } = useOnboardingT();
  const describeError = useApiError();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useOnboarding();
  const [editingGoal, setEditingGoal] = useState(false);

  const restore = useMutation({
    mutationFn: () => api.post('/onboarding/restore'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY }),
  });

  if (isLoading) {
    return <div className="oc-page"><OnboardingSkeleton /></div>;
  }

  if (error) {
    return (
      <div className="oc-page">
        <ErrorNotice
          message={describeError(error)}
          onRetry={() => void refetch()}
          retryLabel={tx('onboarding.retry')}
        />
      </div>
    );
  }
  if (!data) return null;

  /**
   * The goal question takes over the page when it has never been answered.
   * Not a modal: a modal over a dashboard somebody cannot read yet is a
   * dialogue with no context, and it cannot be returned to once dismissed.
   */
  const showChooser = editingGoal || (data.goal === null && data.canChooseGoal);

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('onboarding.title')}</h1>
        <p className="oc-muted mt-1 max-w-prose">{tx('onboarding.subtitle')}</p>
      </header>

      {/* --- The goal ------------------------------------------------------ */}
      <Card>
        {showChooser ? (
          <>
            <CardHeader title={tx('onboarding.ask.title')} />
            <OnboardingGoalChooser data={data} onDone={() => setEditingGoal(false)} />
          </>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-ink-muted mb-1">
                {tx('onboarding.goal.current')}
              </p>
              {data.goal ? (
                <>
                  <p className="font-semibold text-ink">
                    {tx(`onboarding.goal.${data.goal}.title`)}
                  </p>
                  <p className="oc-muted text-sm mt-0.5 max-w-prose">
                    {tx(`onboarding.goal.${data.goal}.body`)}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-ink">{tx('onboarding.goal.unset')}</p>
                  <p className="oc-muted text-sm mt-0.5 max-w-prose">
                    {tx(
                      data.canChooseGoal
                        ? 'onboarding.goal.unsetBody'
                        : 'onboarding.ask.readOnly',
                    )}
                  </p>
                </>
              )}
            </div>
            {data.canChooseGoal && (
              <Button variant="secondary" size="sm" onClick={() => setEditingGoal(true)}>
                {tx('onboarding.goal.change')}
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* --- Hidden notice -------------------------------------------------
          Dismissal is never a one-way door, and the way back must be obvious
          from the place the person went looking. */}
      {data.dismissedAt && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-ink">{tx('onboarding.hidden.title')}</p>
              <p className="oc-muted text-sm mt-0.5 max-w-prose">{tx('onboarding.hidden.body')}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => restore.mutate()}
              disabled={restore.isPending}
            >
              {tx('onboarding.restore')}
            </Button>
          </div>
        </Card>
      )}

      {/* --- The checklist -------------------------------------------------- */}
      <Card>
        <CardHeader
          title={tx('onboarding.title')}
          action={
            data.steps.length > 0 ? (
              <div className="min-w-[10rem]">
                <OnboardingProgressBar data={data} />
              </div>
            ) : undefined
          }
        />

        {data.complete && (
          <div className="mb-4 rounded-xl bg-good/[0.08] border border-good/20 p-3">
            <p className="font-medium text-ink">{tx('onboarding.complete.title')}</p>
            <p className="oc-muted text-sm mt-0.5 max-w-prose">{tx('onboarding.complete.body')}</p>
          </div>
        )}

        <OnboardingChecklist data={data} />
      </Card>

      {/* --- References -----------------------------------------------------
          Deliberately outside the checklist and outside the count. Nothing in
          the database can honestly tick "you know where to report an incident",
          and a step that can never tick is how a progress bar is kept from ever
          finishing. */}
      {data.references.length > 0 && (
        <Card>
          <CardHeader
            title={tx('onboarding.references.title')}
            subtitle={tx('onboarding.references.note')}
          />
          <ul className="divide-y divide-line">
            {data.references.map((ref) => (
              <li key={ref.key} className="py-3 first:pt-0 last:pb-0">
                <Link to={ref.href} className="group block">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-ink group-hover:text-brand transition-colors">
                      {tx(`onboarding.reference.${ref.key}.title`)}
                    </span>
                    <IconChevron
                      className="w-4 h-4 -rotate-90 text-ink-muted group-hover:text-brand transition-colors"
                      aria-hidden
                    />
                  </div>
                  <p className="oc-muted text-sm mt-0.5 max-w-prose">
                    {tx(`onboarding.reference.${ref.key}.body`)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
